import i18n from '@/i18n';
import { accountDayKey, addAccountDays, formatInAccountTz } from '@/lib/accountTimezone';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Relative time labels for notification hubs.
 * Examples: "Agora", "há 5 min", "há 1 h", "Ontem", "há 3 dias"
 *
 * "Ontem" and the fallback date use the account timezone, so the label does not depend on
 * where the operator's browser happens to be. The language is read at call time — this runs
 * outside React, so it cannot use the `useTranslation` hook.
 */
export function formatRelativeTime(isoDate: string, now = new Date()): string {
  const t = i18n.getFixedT(null, 'common');
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = now.getTime() - date.getTime();
  if (diffMs < MINUTE_MS) return t('time.now');

  const minutes = Math.floor(diffMs / MINUTE_MS);
  if (minutes < 60) return t('time.minutesAgo', { count: minutes });

  const hours = Math.floor(diffMs / HOUR_MS);
  if (hours < 24) return t('time.hoursAgo', { count: hours });

  if (accountDayKey(date) === accountDayKey(addAccountDays(now, -1))) return t('time.yesterday');

  const days = Math.floor(diffMs / DAY_MS);
  if (days < 7) return t('time.daysAgo', { count: days });

  return formatInAccountTz(date, {
    day: '2-digit',
    month: 'short',
  });
}
